import React, { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { API } from "../App";
import { useLanguage } from "../i18n/LanguageContext";
import { track, trackHealthSectionSkipped } from "../lib/analytics";
import { needsBirthMonth } from "../lib/subjectAge";
import HealthSectionForm from "./HealthSectionForm";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const RELATIONSHIPS = [
  "child",
  "parent",
  "grandparent",
  "partner",
  "sibling",
  "other_family",
  "other",
];

const CONSENT_OPTIONS = ["guardian", "representative", "subject_consent"];

export default function AddPersonModal({ open, onOpenChange, onCreated }) {
  const { t } = useLanguage();
  const [relationship, setRelationship] = useState("parent");
  const [displayName, setDisplayName] = useState("");
  const [birthYear, setBirthYear] = useState("");
  const [birthMonth, setBirthMonth] = useState("");
  const [sexAtBirth, setSexAtBirth] = useState("");
  const [consentBasis, setConsentBasis] = useState("");
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState("details");
  const [created, setCreated] = useState(null);
  const stepRef = useRef("details");
  const createdRef = useRef(null);
  const safetySavedRef = useRef(false);
  const closingRef = useRef(false);

  stepRef.current = step;
  createdRef.current = created;

  const yearNum = birthYear ? parseInt(birthYear, 10) : null;
  const needsMonth =
    yearNum != null &&
    !Number.isNaN(yearNum) &&
    needsBirthMonth(yearNum, birthMonth || null);

  const reset = () => {
    setRelationship("parent");
    setDisplayName("");
    setBirthYear("");
    setBirthMonth("");
    setSexAtBirth("");
    setConsentBasis("");
    setStep("details");
    setCreated(null);
    safetySavedRef.current = false;
  };

  const leave = (reason) => {
    if (closingRef.current) return;
    closingRef.current = true;
    try {
      const person = createdRef.current;
      const wasSafety = stepRef.current === "safety";
      const saved = safetySavedRef.current;
      createdRef.current = null;
      stepRef.current = "details";
      safetySavedRef.current = false;
      if (wasSafety && person && !saved && (reason === "skip" || reason === "dismiss")) {
        trackHealthSectionSkipped({
          section: "safety",
          surface: "add_person",
          subjectKind: "dependent",
        });
      }
      reset();
      onOpenChange(false);
      if (person) onCreated?.(person);
    } finally {
      closingRef.current = false;
    }
  };

  const handleSubmit = async () => {
    if (!displayName.trim() || !consentBasis) {
      toast.error(t("subjects.addValidation"));
      return;
    }
    if (needsMonth && !birthMonth) {
      toast.error(t("subjects.birthMonthRequired"));
      return;
    }
    setSaving(true);
    try {
      const body = {
        display_name: displayName.trim(),
        relationship,
        consent_basis: consentBasis,
      };
      if (yearNum) body.birth_year = yearNum;
      if (needsMonth && birthMonth) body.birth_month = parseInt(birthMonth, 10);
      if (sexAtBirth) body.sex_at_birth = sexAtBirth;
      const res = await axios.post(`${API}/subjects`, body);
      const next = res.data?.data;
      track("subject_created", { subject_kind: "dependent" });
      toast.success(t("subjects.created"));
      if (!next?.subject_id) {
        reset();
        onOpenChange(false);
        onCreated?.(next);
        return;
      }
      createdRef.current = next;
      stepRef.current = "safety";
      safetySavedRef.current = false;
      setCreated(next);
      setStep("safety");
    } catch (err) {
      const code = err?.response?.data?.error?.code;
      if (code === "subject_limit_reached") {
        toast.error(t("subjects.limitReached"));
      } else {
        toast.error(t("subjects.createError"));
      }
    } finally {
      setSaving(false);
    }
  };

  const onSafety = step === "safety" && created?.subject_id;

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) leave("dismiss");
      }}
    >
      <DialogContent
        className="sm:max-w-md"
        data-testid="add-person-modal"
        {...(onSafety ? {} : { "aria-describedby": undefined })}
      >
        <DialogHeader>
          <DialogTitle>
            {onSafety ? t("subjects.safetyStepTitle") : t("subjects.addPerson")}
          </DialogTitle>
          {onSafety && (
            <DialogDescription>{t("subjects.safetyStepHint")}</DialogDescription>
          )}
        </DialogHeader>
        {onSafety ? (
          <div className="space-y-3 py-1" data-testid="add-person-safety-step">
            <HealthSectionForm
              section="safety"
              subjectId={created.subject_id}
              analytics={{ surface: "add_person", subjectKind: "dependent" }}
              onSaved={() => {
                safetySavedRef.current = true;
              }}
            />
          </div>
        ) : (
          <div className="space-y-3 py-1">
            <div className="space-y-1.5">
              <Label>{t("subjects.relationship")}</Label>
              <Select value={relationship} onValueChange={setRelationship}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RELATIONSHIPS.map((r) => (
                    <SelectItem key={r} value={r}>
                      {t(`subjects.rel.${r}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("subjects.displayName")}</Label>
              <Input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                maxLength={60}
                placeholder={t("subjects.displayNamePlaceholder")}
                data-testid="add-person-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t("healthProfile.birthYear")}</Label>
                <Input
                  type="number"
                  value={birthYear}
                  onChange={(e) => setBirthYear(e.target.value)}
                  placeholder="1944"
                />
              </div>
              {needsMonth && (
                <div className="space-y-1.5">
                  <Label>{t("subjects.birthMonth")}</Label>
                  <Input
                    type="number"
                    min={1}
                    max={12}
                    value={birthMonth}
                    onChange={(e) => setBirthMonth(e.target.value)}
                  />
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>{t("healthProfile.sexAtBirth")}</Label>
              <Select value={sexAtBirth || undefined} onValueChange={setSexAtBirth}>
                <SelectTrigger>
                  <SelectValue placeholder={t("healthProfile.selectPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {["female", "male", "intersex", "prefer_not_to_say"].map((opt) => (
                    <SelectItem key={opt} value={opt}>
                      {t(`healthProfile.sex.${opt}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("subjects.consentTitle")}</Label>
              <p className="text-xs text-muted-foreground">{t("subjects.consentHint")}</p>
              <div className="space-y-2">
                {CONSENT_OPTIONS.map((opt) => (
                  <label key={opt} className="flex items-start gap-2 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name="consent_basis"
                      className="mt-1"
                      checked={consentBasis === opt}
                      onChange={() => setConsentBasis(opt)}
                      data-testid={`add-person-consent-${opt}`}
                    />
                    <span>{t(`subjects.consent.${opt}`)}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          {onSafety ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => leave("skip")}
                data-testid="add-person-skip"
              >
                {t("healthProfile.skip")}
              </Button>
              <Button
                type="button"
                onClick={() => leave("done")}
                data-testid="add-person-done"
              >
                {t("subjects.safetyDone")}
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => leave("cancel")}
                disabled={saving}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={saving}
                data-testid="add-person-submit"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {t("subjects.addPerson")}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
